import _compact from 'lodash/compact';
import { ActionMenu } from 'ui/actionMenu';
import { ActionMenuOption, AllowedActionMenuElements, MENU_ALIGNMENTS } from 'ui/actionMenu/types';
import { BUTTON_VARIANTS, IconButton } from 'ui/button';
import {
  Archive,
  DisableAlert,
  Edit16,
  EditDesign16,
  Favorite16,
  MakeCopy,
  MoveToFolder,
  Overflow,
  PerformanceAlerts16,
  Undo,
} from 'ui/icons';
import { colors } from 'ui/theme';

import { MessageMedium, WhatsApp } from 'constants/messageMediums';
import {
  selectIsCampaignPerformanceAlertsFeatureFlagOn,
  selectIsCampaignPerformanceAlertsHistoryFeatureFlagOn,
  selectIsCampaignsFavoritesFeatureFlagOn,
} from 'core/userContext';
import { getIsWhatsAppCampaign } from 'modules/campaigns/lib/utils';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { useReactCampaignV1Feature } from 'utils/hooks/featureFlags';
import { SavedAlertsByMedium } from '../../campaigns/alerts/types';
import { CampaignStateDefinitions, GraphQLCampaign } from '../domainTypes';
import { useCampaignFolderModals } from '../hooks/useCampaignFolderModals';
import { hasProjectAlerts, isNotYetRunning, wasArchived } from '../utils';
import {
  CampaignIndexModalType,
  useCampaignIndexModalContext,
} from './contexts/CampaignIndexModalContext';
import { useSelectionContext } from './contexts/SelectionContext';

export const NoStrokeDisableAlert = styled(DisableAlert)`
  && path {
    stroke: none;
  }
`;

export const displayContent = {
  archive: 'Archive',
  copy: 'Make a copy',
  disableAlerts: 'Disable alerts',
  edit: 'Edit campaign',
  editDetails: 'Edit details',
  move: 'Move to folder',
  unarchive: 'Unarchive',
  viewAlerts: 'View alerts',
};

type CampaignOverflowMenuProps = {
  campaign: GraphQLCampaign;
  onViewAlerts: () => void;
  projectAlerts: SavedAlertsByMedium;
  isFavoriteSelected: boolean;
  onFavoriteClick: () => void;
};

export const CampaignOverflowMenu = ({
  campaign,
  onViewAlerts,
  projectAlerts,
  isFavoriteSelected,
  onFavoriteClick,
}: CampaignOverflowMenuProps) => {
  const history = useHistory();
  const isCampaignPerformanceAlertsFeatureEnabled = useSelector(
    selectIsCampaignPerformanceAlertsFeatureFlagOn,
  );
  const isCampaignPerformanceAlertsHistoryFeatureEnabled = useSelector(
    selectIsCampaignPerformanceAlertsHistoryFeatureFlagOn,
  );
  const isCampaignsFavoritesFeatureFlagOn = useSelector(selectIsCampaignsFavoritesFeatureFlagOn);

  const isReactCampaignV1FeatureOn = useReactCampaignV1Feature();
  const { selectItem, clearAll } = useSelectionContext();
  const modals = useCampaignFolderModals();
  const { openModal } = useCampaignIndexModalContext();

  const setCampaignAsCurrentSelection = () => {
    clearAll();
    selectItem(campaign);
  };

  const isArchived = wasArchived(campaign);
  const isNotLaunched = isNotYetRunning(campaign);
  const hasCampaignAlertsEnabled = !!campaign?.campaignAlertsEnabled;
  const isNotFinishedCampaign = campaign.campaignState !== CampaignStateDefinitions.Finished;
  const isProjectAlertEnabled = hasProjectAlerts(
    campaign.campaignTypeDefinition,
    projectAlerts[campaign.messageMedium],
  );
  const supportsTemplateDetailsView = [MessageMedium.Email, MessageMedium.InApp, WhatsApp].includes(
    campaign.messageMedium,
  );
  const isWhatsApp = getIsWhatsAppCampaign(campaign.messageMedium);

  const overflowOptions: ActionMenuOption[] = _compact([
    isNotLaunched &&
      isReactCampaignV1FeatureOn && {
        as: AllowedActionMenuElements.Button,
        content: (
          <>
            <Edit16 color={colors.grey50} />
            <span>{displayContent.edit}</span>
          </>
        ),
        id: 'edit-campaign',
        onClick: () => {
          history.push(`/campaigns/${campaign.id}?view=Edit`);
        },
      },
    isCampaignsFavoritesFeatureFlagOn && {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <Favorite16 color={colors.grey50} />
          <span>{isFavoriteSelected ? 'Remove from' : 'Add to'} favorites</span>
        </>
      ),
      id: 'edit-favorite',
      onClick: onFavoriteClick,
    },
    isNotLaunched &&
      !isReactCampaignV1FeatureOn && {
        as: AllowedActionMenuElements.Link,
        content: (
          <>
            <Edit16 color={colors.grey50} />
            <span>{displayContent.edit}</span>
          </>
        ),
        id: 'edit-campaign',
        href: `/campaigns/create?page=1&count=5&sorting%5Bid%5D=desc&campaignId=${campaign?.id}`,
        target: '_blank',
        isExternalLink: false,
      },
    isNotLaunched &&
      supportsTemplateDetailsView && {
        as: AllowedActionMenuElements.Button,
        content: (
          <>
            <EditDesign16 color={colors.grey50} />
            <span>{displayContent.editDetails}</span>
          </>
        ),
        id: 'edit-campaign-details',
        onClick: () => {
          history.push(`/campaigns/${campaign.id}?templateEditor=open`);
        },
      },
    !isWhatsApp && {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <MakeCopy height={16} color={colors.grey50} />
          <span>{displayContent.copy}</span>
        </>
      ),
      id: 'make-a-copy',
      onClick: () => {
        setCampaignAsCurrentSelection();
        modals.copy.toggle(true);
      },
    },
    !isArchived && {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <MoveToFolder height={18} color={colors.grey50} />
          <span>{displayContent.move}</span>
        </>
      ),
      id: 'move',
      onClick: () => {
        setCampaignAsCurrentSelection();
        modals.move.toggle(true);
      },
    },
    isCampaignPerformanceAlertsFeatureEnabled &&
      isCampaignPerformanceAlertsHistoryFeatureEnabled &&
      !isWhatsApp && {
        as: AllowedActionMenuElements.Button,
        content: (
          <>
            <PerformanceAlerts16 color={colors.grey50} />
            <span>{displayContent.viewAlerts}</span>
          </>
        ),
        id: 'view-alerts',
        onClick: onViewAlerts,
      },
    isCampaignPerformanceAlertsFeatureEnabled &&
      hasCampaignAlertsEnabled &&
      isProjectAlertEnabled &&
      isNotFinishedCampaign && {
        as: AllowedActionMenuElements.Button,
        content: (
          <>
            <NoStrokeDisableAlert color={colors.grey50} />
            <span>{displayContent.disableAlerts}</span>
          </>
        ),
        id: 'disable-alerts',
        onClick: () => {
          openModal(CampaignIndexModalType.ConfirmDisableAlertsModal, {
            campaignId: campaign?.id,
            campaignName: campaign?.name,
          });
        },
      },
    !isArchived && {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <Archive height={16} color={colors.grey50} />
          <span>{displayContent.archive}</span>
        </>
      ),
      id: 'archive',
      onClick: () => {
        setCampaignAsCurrentSelection();
        modals.archive.toggle(true);
      },
    },
    isArchived && {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <Undo height={16} color={colors.grey50} />
          <span>{displayContent.unarchive}</span>
        </>
      ),
      id: 'unarchive',
      onClick: () => {
        setCampaignAsCurrentSelection();
        modals.unarchive.toggle(true);
      },
    },
  ]);

  return (
    <ActionMenu align={MENU_ALIGNMENTS.RIGHT} options={overflowOptions}>
      <IconButton variant={BUTTON_VARIANTS.SECONDARY}>
        <Overflow color={colors.grey90} />
      </IconButton>
    </ActionMenu>
  );
};
